<template>
  <div class="ml-6 h-full flex flex-col">
    <div class="py-1 sm:flex sm:items-center sm:justify-between border-b">
      <div class="w-full">
        <div class="flex items-center">
          <div class="grow">
            <!--
            <div v-if="kbDocumentTitle" class="leading-6 font-source text-xl font-medium text-gray-700">{{ kbDocumentTitle }}</div>
            -->
            <div class="flex items-center">
              <mdicon name="file-document-outline" size="20" class="mr-1 text-gray-400 rounded" aria-hidden="true"></mdicon>
              <div :class="['text-md text-slate-600', 'font-source font-medium leading-6']">
                <a v-if="kbDocumentName.startsWith('http')" class="hover:underline cursor-pointer" :href="kbDocumentName" target="_blank">{{ kbDocumentName }}</a>
                <h1 v-else>{{ kbDocumentName }}</h1>
              </div>
              <ClipboardCopyButton @click="onCopyName"></ClipboardCopyButton>
            </div>
            <!--
            <div class="mb-1 text-xs">
              Updated <time :datetime="kbStore.currentKbDocument?.updated">{{ dayjs(kbStore.currentKbDocument?.updated).format('LLL') }}</time> By {{ kbStore.currentKbDocument?.updatedBy }}
            </div>
            -->
          </div>
          <SelectButton
            v-if="showContentVersionSelector"
            v-tooltip.top="'Content version ...'"
            v-model="selectedContentVersion"
            :options="contentVersionOptions"
            optionLabel="title"
            optionValue="value"
            :allowEmpty="false"
            aria-labelledby="basic"
            size="small"
            @change="async () => await onChangeContentVersion()"
          />
          <Button class="mx-2" icon="pi pi-cog" :severity="showSettings ? 'primary' : 'secondary'" aria-label="Settings" size="small" @click="showSettings = !showSettings" />
        </div>
        <div v-if="kbStore.currentKbDocument" class="mr-1 max-w-[800px]">
          <StdTagInputEditor v-model="kbDocumentTagsValue" :available-tags="kbStore.kbTags" @add-tag="onKbAddTag"></StdTagInputEditor>
        </div>
      </div>
      <div v-if="false" class="mr-8">
        <ButtonGroup>
          <Button label="Find" severity="secondary" icon="pi pi-search" outlined size="small" @click="onShowFind" />
          <Button label="Replace" severity="secondary" icon="pi pi-search-plus" outlined size="small" @click="onShowReplace" />
        </ButtonGroup>
      </div>
    </div>

    <div v-if="false" class="flex-auto">
      <v-ace-editor ref="editorElement" v-model:value="kbDocumentContentValue" lang="markdown" theme="crimson_editor" :options="editorOptions" style="height: 100%" />
    </div>

    <div class="flex-auto flex items-start">
      <component :is="selectedContentVersion === KB_DOC_VERSION.DRAFT ? TuiEditor : TuiViewer" class="flex-grow" ref="mdEditorElement" :initial-value="kbDocumentContentValue" height="100%" />
      <div v-if="showSettings" class="w-1/3 h-full py-3 px-4">
        <div class="flex items-center pb-2 border-b w-full">
          <mdicon name="cog" size="22" class="text-slate-300 mr-1" aria-hidden="true"></mdicon>
          <div class="text-xl font-semibold text-slate-600 leading-6">Document Settings</div>
        </div>
        <div class="mt-4">
          <div class="text-md font-medium text-slate-700 mb-2">Split Mode</div>
          <Dropdown class="w-full" v-model="kbDocumentSplitValue" :options="splitModes" optionLabel="name" optionValue="value" placeholder="Select Split Mode">
            <template #option="slotProps">
              <div class="flex flex-col align-items-center">
                <div>{{ slotProps.option.name }}</div>
                <div class="text-xs">{{ slotProps.option.description }}</div>
              </div>
            </template>
          </Dropdown>
          <div v-if="kbDocumentSplitValue === 'semantic'" class="mt-4">
            <!-- <div class="text-md font-medium text-slate-700 mb-2">Document Parts</div> -->
            <Button class="w-full p-button-outlined" severity="secondary" @click="showDocumentPartsDropdown = !showDocumentPartsDropdown">
              <div class="flex items-center justify-between w-full">
                <span>Document Parts</span>
                <mdicon :name="showDocumentPartsDropdown ? 'chevron-up' : 'chevron-down'" size="20" class="ml-2" />
              </div>
            </Button>
            <div v-if="showDocumentPartsDropdown" class="mt-2 border rounded-md p-2 bg-white shadow-sm max-h-[300px] overflow-auto">
              <div v-for="part in documentPartsEntries" :key="part.id" class="p-2 hover:bg-slate-100 rounded cursor-pointer">
                <div class="flex flex-col">
                  <div class="flex items-center">
                    <div class="flex flex-col">
                      <span class="text-xs text-slate-500 mr-2">ID: {{ part.id }}</span>
                      <span class="text-xs text-slate-500 mr-2">Size: {{ part.sizeBytes }} bytes</span>
                    </div>
                    <span class="text-sm text-blue-600 hover:underline font-medium" @click="openPartViewer(part)">
                      {{ part.title || 'Untitled part' }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="text-md font-medium text-slate-700 mt-4 mb-2">Metadata</div>
          <div v-for="(entry, idx) in documentMetadataEntries" :key="idx" class="flex flex-col mb-2">
            <div class="text-xs text-slate-500">{{ entry.name }}</div>
            <div class="text-sm text-slate-700">{{ entry.value }}</div>
          </div>
          <div v-if="kbDocumentSplitValue === 'semantic'" class="flex flex-col mb-2">
            <div class="text-xs text-slate-500">Document parts</div>
            <div class="text-sm text-slate-700">{{ documentPartsEntries.length }}</div>
          </div>
        </div>
      </div>
    </div>
    <div v-if="false" class="flex-auto">
      <TuiViewer ref="mdEditorElement" :initial-value="kbDocumentContentValue" class="h-[400px] overflow-auto" />
    </div>

    <!-- Document Part Viewer Dialog -->
    <KbDialogDocumentPartViewer :open="showPartViewerDialog" :title="selectedPartTitle" :content="selectedPartContent" :partId="selectedPartId" @close="showPartViewerDialog = false" />

    <!--
    <ConfirmationDialog :open="deleteConfirm" title="Delete" :description="`Are you sure you want to delete selected Knowledge Bases ?`" @cancel="deleteConfirm = false" @confirm="onDeleteConfirmed"></ConfirmationDialog>
    <ImportFileDialog ref="importSpecDialog" @cancel="onImportCancel" @import="onSpecImport"></ImportFileDialog>
    -->
  </div>
</template>

<script setup>
  import { useRoute } from 'vue-router';
  import { useKbStore } from '@/store/KbStore';
  import { useProgress } from '@/hooks/useProgress';
  import { useToast } from 'vue-toastification';
  import { computed, onMounted, ref, watch, onBeforeUnmount } from 'vue';
  import ClipboardCopyButton from '@/components/Buttons/ClipboardCopyButton.vue';
  import StdTagInputEditor from '@/components/Editors/StdTagInputEditor.vue';
  import ButtonGroup from 'primevue/buttongroup';
  import Button from 'primevue/button';
  import SelectButton from 'primevue/selectbutton';
  import Dropdown from 'primevue/dropdown';
  import TuiEditor from '@/components/Editors/TuiEditor.vue';
  import TuiViewer from '@/components/Editors/TuiViewer.vue';
  import KbDialogDocumentPartViewer from '@/components/KB/dialogs/KbDialogDocumentPartViewer.vue';
  import { VAceEditor } from 'vue3-ace-editor';
  import 'ace-builds/src-noconflict/mode-markdown';
  import 'ace-builds/src-noconflict/theme-crimson_editor';
  import 'ace-builds/src-noconflict/theme-dreamweaver';
  import 'ace-builds/src-noconflict/ext-searchbox';
  import './acemodelog.js';
  import './acemodekbdoc.js';
  import dayjs from 'dayjs';
  import timezone from 'dayjs/plugin/timezone';
  import localizedFormat from 'dayjs/plugin/localizedFormat';
  import { KB_DOC_VERSION } from '@enegelai/core';

  dayjs.extend(localizedFormat);
  dayjs.extend(timezone);

  const route = useRoute();
  const kbStore = useKbStore();
  const toast = useToast();
  const progress = useProgress();

  const mdEditorElement = ref(null);

  const editorElement = ref(null);
  let editor = null;
  const editorOptions = {
    fontSize: 18,
    wrap: true,
  };

  const kbId = ref();
  const kbDocumentId = ref();

  const kbDocumentName = computed(() => kbStore.currentKbDocument?.name || kbDocumentId.value || '');

  const showSettings = ref(false);
  const showDocumentPartsDropdown = ref(false);
  const showPartViewerDialog = ref(false);
  const selectedPartTitle = ref('');
  const selectedPartContent = ref('');
  const selectedPartId = ref('');

  const splitModes = ref([
    { name: 'Default', value: 'default', description: 'Split to chunks, use each chunk separately' },
    { name: 'Single Part', value: 'singlepart', description: 'Split to chunks, use whole document if any chunk matched' },
    { name: 'Semantic', value: 'semantic', description: "Split document into semantic parts, use whole part if any part's chunk matched" },
    { name: 'No Split', value: 'nosplit', description: 'Do not split to chunks, always search for and use whole document' },
  ]);

  const documentMetadataEntries = computed(() => {
    const meta = kbStore.currentKbDocument?.metadata || null;
    if (!meta || typeof meta !== 'object') {
      return [];
    }
    return Object.entries(meta).map(([key, value]) => {
      const e = {
        name: key,
        value: Array.isArray(value) ? value.join(',') : value,
      };
      return e;
    });
  });

  const documentPartsEntries = computed(() => {
    if (!kbStore.currentKbDocumentParts || !Array.isArray(kbStore.currentKbDocumentParts)) {
      return [];
    }
    return kbStore.currentKbDocumentParts.map((part) => {
      const e = {
        id: part.id,
        title: part?.metadata?.title || '',
        summary: part?.metadata?.summary || '',
        startLineNumber: part?.metadata?.startLineNumber || 0,
        endLineNumber: part?.metadata?.endLineNumber || 0,
        sizeBytes: part?.metadata?.sizeBytes || 0,
        pageContent: part?.pageContent || '',
      };
      return e;
    });
  });

  const showContentVersionSelector = computed(() => kbStore.contentPublishingEnabled && kbStore.currentKbDocument?.status !== 'published');
  const contentVersionOptions = computed(() => {
    const opts = [
      { title: 'Draft', value: KB_DOC_VERSION.DRAFT },
      { title: 'Published', value: KB_DOC_VERSION.CURRENT },
    ];
    return opts;
  });

  const selectedContentVersion = ref(kbStore.contentPublishingEnabled ? KB_DOC_VERSION.DRAFT : KB_DOC_VERSION.CURRENT);

  async function onChangeContentVersion() {
    await swapCurrentKbDocumentContent(selectedContentVersion.value);
  }

  const kbDocumentContentValue = computed({
    get() {
      return kbStore.currentKbDocumentContent || '';
    },
    set(value) {
      kbStore.currentKbDocumentContent = value;
    },
  });

  const kbDocumentTagsValue = computed({
    get() {
      return kbStore.currentKbDocumentTags || [];
    },
    set(value) {
      kbStore.setTagsForCurrentKbDocument(value);
    },
  });

  const kbDocumentSplitValue = computed({
    get() {
      return kbStore.currentKbDocumentSplit || 'default';
    },
    set(value) {
      kbStore.setSplitForCurrentKbDocument(value);
    },
  });

  function openPartViewer(part) {
    selectedPartTitle.value = part.title || `Part ${part.id}`;
    selectedPartContent.value = part.pageContent || '';
    selectedPartId.value = part.id;
    showPartViewerDialog.value = true;
  }

  watch(
    () => kbDocumentContentValue.value,
    function (mdValue) {
      console.log('value changes detected');
      mdEditorElement.value.setMarkdown(mdValue, false);
    },
  );

  async function initKbDocumentEditor() {
    kbId.value = route.params?.id || null;
    kbDocumentId.value = route.params?.documentId || null;
    if (!kbId.value || !kbDocumentId.value) {
      console.log(`ERROR: KB ID and KB Document ID must be specified`);
      toast.error(`Missing URL parameters`);
      return;
    }
    await loadKbDocument(kbId.value, kbDocumentId.value, selectedContentVersion.value);
  }

  async function loadKbDocument(kbId, kbDocumentId, contentVersion) {
    progress.startProgress({ blockUI: true, spinner: true });
    await kbStore.loadKbDocument(kbId, kbDocumentId);
    await swapCurrentKbDocumentContent(contentVersion);
    progress.stopProgress();
    console.log(`KB[${kbId}]: Loaded KB doc:${kbDocumentId}`);
  }

  async function swapCurrentKbDocumentContent(contentVersion) {
    progress.startProgress({ blockUI: true, spinner: true });
    const content = await kbStore.swapCurrentKbDocumentContent(contentVersion);
    kbDocumentContentValue.value = content;
    progress.stopProgress();
    console.log(`KB[${kbId.value}]: Swapped KB doc:${kbDocumentId.value}, version:${contentVersion}`);
  }

  function onCopyName() {
    navigator.clipboard.writeText(kbDocumentName.value);
  }

  function onShowFind() {
    if (editor) {
      editor.execCommand('find');
    }
  }

  function onShowReplace() {
    if (editor) {
      editor.execCommand('replace');
    }
  }

  // Subscribe to actions in kbStore
  kbStore.$onAction(
    ({
      name, // name of the action
    }) => {
      if (name === 'beforeSaveKbDocument') {
        // Need to update store with the most recent document content
        const md = mdEditorElement.value.getMarkdown();
        kbStore.updateCurrentKbDocumentContent(selectedContentVersion.value, md);
        console.log(`KbDocumentEditor: kbStore.beforeSaveKbDocument - updated document content`);
      }
    },
  );

  function onKbAddTag(tag) {
    kbStore.addTagToKb(tag);
  }

  onMounted(async () => {
    await initKbDocumentEditor();
    //initEditor();
  });

  onBeforeUnmount(() => {
    kbStore.unloadKbDocument();
  });
</script>
